import { useState, useEffect, useRef } from 'react'
import type { Drink, DrinkEntry } from '@/types'
import Modal from '@/components/layout/Modal'
import DrinkSelector from './DrinkSelector'
import { formatDateTimeLocal, formatDateForStorage, volumeUnit, mlToDisplay, displayToMl, isImperial } from '@/utils/time'
import { ETHANOL_DENSITY } from '@/utils/bac'

// Canonical AI-lookup name format: "<Brand+Variant> <volume>ML <abv>%"
const CANONICAL_NAME_RE = /^(.+?)\s+\d+(?:\.\d+)?\s*ML\s+\d+(?:\.\d+)?\s*%\s*$/i

const KCAL_PER_G_ETHANOL = 7
const ETHANOL_KCAL_PER_ML = ETHANOL_DENSITY * KCAL_PER_G_ETHANOL // ≈ 5.523

function updateCanonicalName(currentName: string, mlValue: number, abv: number): string {
  const match = currentName.match(CANONICAL_NAME_RE)
  if (!match) return currentName
  const baseName = match[1].trim()
  const abvStr = (Math.round(abv * 10) / 10).toFixed(1)
  return `${baseName} ${Math.round(mlValue)}ML ${abvStr}%`
}

function recalcCalories(
  baseMl: number,
  baseAbv: number,
  baseCalories: number,
  newMl: number,
  newAbv: number,
): number {
  if (baseMl <= 0 || baseCalories <= 0) return baseCalories
  const baseAlcCal = baseMl * (baseAbv / 100) * ETHANOL_KCAL_PER_ML
  const baseNonAlcCal = Math.max(0, baseCalories - baseAlcCal)
  const baseNonAlcCalPerMl = baseNonAlcCal / baseMl
  const newAlcCal = newMl * (newAbv / 100) * ETHANOL_KCAL_PER_ML
  const newNonAlcCal = baseNonAlcCalPerMl * newMl
  return Math.max(0, Math.round(newAlcCal + newNonAlcCal))
}

interface DrinkFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (entry: DrinkEntry) => void
  editingEntry?: DrinkEntry | null
  editingIndex?: number | null
  myDrinks: Drink[]
  allDrinks: Drink[]
  lastDrink?: Drink | null
  lastDrinkTime?: string | null
}

export default function DrinkForm({
  isOpen,
  onClose,
  onSave,
  editingEntry,
  myDrinks,
  allDrinks,
  lastDrink,
  lastDrinkTime,
}: DrinkFormProps) {
  const [drink, setDrink] = useState<Drink>({ drink: '', milliliters: '', alcohol: '', calories: '' })
  const [finishedAt, setFinishedAt] = useState('')
  const [validationError, setValidationError] = useState('')
  // Base drink reference, always stored in actual ml (not display units) so calorie math is unit-agnostic
  const baseDrinkRef = useRef<{ ml: number; abv: number; calories: number } | null>(null)

  /** Convert a display-unit volume (fl oz for US, ml otherwise) back to actual ml. */
  const displayVolToMl = (displayVol: number): number =>
    isImperial() ? displayToMl(displayVol) : displayVol

  /** Convert a Drink's milliliters from ml to display units */
  const toDisplayDrink = (d: Drink): Drink => {
    if (!isImperial()) return d
    const ml = parseFloat(d.milliliters)
    if (isNaN(ml)) return d
    return { ...d, milliliters: mlToDisplay(ml).toString() }
  }

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setValidationError('')
      if (editingEntry) {
        // Editing existing entry (stored in ml, convert to display)
        const displayDrink = toDisplayDrink(editingEntry.drink)
        setDrink(displayDrink)
        setFinishedAt(formatDateTimeLocal(editingEntry.finished_at))
        baseDrinkRef.current = {
          ml: parseFloat(editingEntry.drink.milliliters) || 0,
          abv: parseFloat(editingEntry.drink.alcohol) || 0,
          calories: parseFloat(editingEntry.drink.calories) || 0,
        }
      } else if (lastDrink) {
        // New entry - pre-populate with last drink (stored in ml, convert to display)
        const displayDrink = toDisplayDrink(lastDrink)
        setDrink(displayDrink)
        setFinishedAt(formatDateTimeLocal(new Date()))
        baseDrinkRef.current = {
          ml: parseFloat(lastDrink.milliliters) || 0,
          abv: parseFloat(lastDrink.alcohol) || 0,
          calories: parseFloat(lastDrink.calories) || 0,
        }
      } else {
        // New entry - empty
        setDrink({ drink: '', milliliters: '', alcohol: '', calories: '' })
        setFinishedAt(formatDateTimeLocal(new Date()))
        baseDrinkRef.current = null
      }
    }
  }, [isOpen, editingEntry, lastDrink])

  const handleDrinkChange = (newDrink: Drink) => {
    // Drinks from selector are in ml, convert to display units for the form
    const displayDrink = toDisplayDrink(newDrink)
    setDrink(displayDrink)
    baseDrinkRef.current = {
      ml: parseFloat(newDrink.milliliters) || 0,
      abv: parseFloat(newDrink.alcohol) || 0,
      calories: parseFloat(newDrink.calories) || 0,
    }
  }

  const handleVolumeOrAbvChange = (
    nextVolume: string,
    nextAlcohol: string,
  ) => {
    setDrink(prev => {
      const next: Drink = { ...prev, milliliters: nextVolume, alcohol: nextAlcohol }

      const displayVol = parseFloat(nextVolume)
      const newAbv = parseFloat(nextAlcohol)
      if (!isNaN(displayVol) && displayVol > 0 && !isNaN(newAbv) && newAbv >= 0) {
        const newMl = displayVolToMl(displayVol)

        // Auto-update canonical title (only if it matches the AI-lookup format)
        next.drink = updateCanonicalName(prev.drink, newMl, newAbv)

        // Recalculate calories from base drink, factoring in both volume and ABV change
        const base = baseDrinkRef.current
        if (base && base.ml > 0 && base.calories > 0) {
          next.calories = recalcCalories(base.ml, base.abv, base.calories, newMl, newAbv).toString()
        }
      }

      return next
    })
  }

  const handleMillilitersChange = (newMl: string) => {
    handleVolumeOrAbvChange(newMl, drink.alcohol)
  }

  const handleAlcoholChange = (newAbv: string) => {
    handleVolumeOrAbvChange(drink.milliliters, newAbv)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError('')

    if (!drink.drink.trim()) {
      setValidationError('Please enter a drink name')
      return
    }

    const displayVol = parseFloat(drink.milliliters)
    if (!drink.milliliters || isNaN(displayVol) || displayVol <= 0) {
      setValidationError(`Volume must be greater than 0 ${volumeUnit()}`)
      return
    }
    // Convert display volume back to ml for storage
    const mlValue = isImperial() ? displayToMl(displayVol) : displayVol
    if (mlValue > 10000) {
      setValidationError(`Volume too large (max ~${mlToDisplay(10000)} ${volumeUnit()})`)
      return
    }

    const abv = parseFloat(drink.alcohol)
    if (!drink.alcohol || isNaN(abv) || abv < 0 || abv > 100) {
      setValidationError('Alcohol % must be between 0 and 100')
      return
    }

    if (drink.calories) {
      const cal = parseFloat(drink.calories)
      if (isNaN(cal) || cal < 0) {
        setValidationError('Calories cannot be negative')
        return
      }
    }

    const entry: DrinkEntry = {
      finished_at: formatDateForStorage(new Date(finishedAt)),
      drink: {
        drink: drink.drink.trim(),
        milliliters: Math.round(mlValue).toString(),
        alcohol: drink.alcohol.replace(/[^0-9.]/g, ''),
        calories: drink.calories.replace(/[^0-9.]/g, ''),
      },
    }

    onSave(entry)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingEntry ? 'Edit Drink' : 'Log Drink'}
      transparent={!!editingEntry}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="drink-form" className="btn btn-primary">
            {editingEntry ? 'Update' : 'Log Drink'}
          </button>
        </>
      }
    >
      <form id="drink-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Drink Selector */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Drink
          </label>
          <DrinkSelector
            value={drink}
            onChange={handleDrinkChange}
            myDrinks={myDrinks}
            allDrinks={allDrinks}
          />
        </div>

        {/* Volume & ABV */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Volume ({volumeUnit()})
            </label>
            <input
              type="number"
              value={drink.milliliters}
              onChange={(e) => handleMillilitersChange(e.target.value)}
              placeholder="330"
              className="w-full"
              min="0"
              step="1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Alcohol (%)
            </label>
            <input
              type="number"
              value={drink.alcohol}
              onChange={(e) => handleAlcoholChange(e.target.value)}
              placeholder="5.0"
              className="w-full"
              min="0"
              max="100"
              step="0.1"
              required
            />
          </div>
        </div>

        {/* Calories */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Calories (optional)
          </label>
          <input
            type="number"
            value={drink.calories}
            onChange={(e) => setDrink(prev => ({ ...prev, calories: e.target.value }))}
            placeholder="150"
            className="w-full"
            min="0"
          />
        </div>

        {/* Validation error */}
        {validationError && (
          <p className="text-sm text-[var(--bac-danger)]">{validationError}</p>
        )}

        {/* Date/Time */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Finished At
          </label>
          <input
            type="datetime-local"
            value={finishedAt}
            onChange={(e) => setFinishedAt(e.target.value)}
            className="w-full"
            required
          />
          {/* Time shortcuts */}
          <div className="flex flex-wrap gap-2 mt-2">
            {lastDrinkTime && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const lastTime = new Date(lastDrinkTime)
                    lastTime.setMinutes(lastTime.getMinutes() - 30)
                    setFinishedAt(formatDateTimeLocal(lastTime))
                  }}
                  className="px-3 py-1 text-xs rounded-full bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                >
                  {editingEntry ? 'Prev-30' : 'Last-30'}
                </button>
                <button
                  type="button"
                  onClick={() => setFinishedAt(formatDateTimeLocal(lastDrinkTime))}
                  className="px-3 py-1 text-xs rounded-full bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                >
                  {editingEntry ? 'Prev' : 'Last'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const lastTime = new Date(lastDrinkTime)
                    lastTime.setMinutes(lastTime.getMinutes() + 30)
                    setFinishedAt(formatDateTimeLocal(lastTime))
                  }}
                  className="px-3 py-1 text-xs rounded-full bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                >
                  {editingEntry ? 'Prev+30' : 'Last+30'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setFinishedAt(formatDateTimeLocal(new Date()))}
              className="px-3 py-1 text-xs rounded-full bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
            >
              Now
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
